import React from 'react'

import Separator from '../../../../../components/pdf/Separator'
import View from '../../../../../components/pdf/View'
import { Bullets, Paragraph, SectionHeading, SubHeading, SubSubHeading } from './_shared/Prose'

/**
 * Seção 7 - Metodologia (Físico/Químico/Biológico/Ergonômico-Biomecânico/Acidente e
 * Ergonômico/Psicossocial). Texto literal do modelo aprovado por Julia Buzzo ("Modelo
 * PGR 2026 - Banco Bradesco_Varejo"). Substitui o placeholder institucional anterior
 * (arquivo era `07.LevantamentoPreliminarSection.js` — o conteúdo de "Análise
 * Preliminar de Perigo e Riscos" é a subseção 7.2.1 abaixo, não uma seção própria).
 */
const MetodologiaFisicoQuimicoErgonomicoSection = () => (
  <View id="7">
    <Separator weight={15} />
    <SectionHeading numero={7}>
      METODOLOGIA (FÍSICO/QUÍMICO/BIOLÓGICO/ERGONÔMICO-BIOMECÂNICO/ACIDENTE E ERGONÔMICO/PSICOSSOCIAL)
    </SectionHeading>

    <SubHeading id="7.1">7.1. Metodologia: Físico, Químico, Biológico, Ergonômico/Biomecânico e Acidente</SubHeading>

    <SubSubHeading>7.1.1 Antecipação e Reconhecimento dos Riscos</SubSubHeading>
    <Paragraph>
      O Banco Bradesco S.A evidencia seu compromisso com a saúde e segurança no trabalho por meio da adoção de uma
      metodologia estruturada para o reconhecimento e avaliação dos níveis de risco, bem como para a implementação
      de medidas de controle direcionadas a esses fatores. Este processo de avaliação considera os requisitos legais
      aplicáveis, as manifestações dos funcionários, além dos perigos específicos de cada área e função,
      identificados por meio de inspeções in loco.
    </Paragraph>
    <Paragraph>
      A partir dessa análise, são determinados os fatores de risco significativo, com especial atenção aos de maior
      relevância, que orientam a definição dos objetivos e metas estratégicas para a segurança e saúde ocupacional.
    </Paragraph>

    <SubSubHeading>7.1.2 Caracterização da Exposição dos Trabalhadores</SubSubHeading>
    <Paragraph>
      A caracterização da exposição dos trabalhadores no Banco Bradesco S.A. é realizada considerando o setor de
      atuação e o cargo/função exercido. Essa abordagem permite uma análise mais precisa dos perigos e riscos
      ocupacionais, conforme previsto no item 1.5.4 da NR-01, que trata da identificação de perigos e avaliação de
      riscos.
    </Paragraph>
    <Paragraph>
      Os documentos de Segurança e Saúde no Trabalho (SST), incluindo o Inventário de Riscos e o Plano de Ação, são
      elaborados com base nas especificidades de cada função e ambiente laboral, assegurando que as medidas de
      prevenção sejam direcionadas e eficazes.
    </Paragraph>
    <Paragraph>
      Essa metodologia atende às exigências legais e facilita a gestão dos riscos. A adoção dessa estrutura
      organizacional visa contemplar as particularidades de cada dependência, garantindo que os processos de
      avaliação e controle sejam compatíveis com as condições reais de exposição dos trabalhadores, conforme
      orientações da NR-01 e do Gerenciamento de Riscos Ocupacionais (GRO).
    </Paragraph>

    <SubSubHeading>7.1.3 Caracterização dos Agentes presentes no Ambiente de Trabalho</SubSubHeading>
    <Paragraph>
      Após a caracterização do ambiente de trabalho é necessário realização da caracterização dos agentes, isto é,
      entender quais riscos (agentes físicos, químicos, biológicos, ergonômicos/biomecânico e acidentes) que estão
      presentes em determinado ambiente de trabalho e como esses agentes podem afetar a saúde dos trabalhadores, com
      objetivo de propor as medidas de controle mais adequadas a cada situação.
    </Paragraph>

    <SubHeading id="7.2">7.2. Metodologia: Ergonômico/Psicossocial</SubHeading>
    <Paragraph>
      Este Programa abrangerá os perigos e riscos identificados no ambiente de trabalho da empresa, conforme
      estabelecido na NR 1 da Portaria MTE nº 1.419, de 27 de agosto de 2024. A redação da Norma Regulamentadora nº
      1 (NR-1) estabelece que o Gerenciamento de Riscos Ocupacionais (GRO) deve ser implementado por meio de um
      Programa de Gerenciamento de Riscos (PGR), abrangendo todas as formas de exposição relacionadas à atividade
      laboral.
    </Paragraph>
    <Paragraph>
      No tocante à metodologia exigida, a NR-1 determina, no item 1.5.4.4.2, que para cada risco identificado deve
      ser indicado o nível de risco ocupacional, o qual deve ser determinado pela combinação entre a severidade das
      possíveis lesões ou agravos à saúde e a probabilidade de sua ocorrência.
    </Paragraph>
    <Paragraph>
      Para os fatores ergonômicos e psicossociais (como conflitos organizacionais, sobrecarga emocional, falta de
      autonomia ou assédio, entre outros), a determinação da probabilidade de ocorrência da doença requer uma
      abordagem científica que considere tanto as exigências da atividade de trabalho quanto a eficácia das medidas
      preventivas adotadas, conforme estabelece o item 1.5.4.4.5.3. Ou seja, deve-se considerar o conteúdo das
      tarefas, os ritmos impostos, os conflitos organizacionais, a carga emocional das funções e o ambiente
      relacional, entre outros elementos.
    </Paragraph>
    <Paragraph>
      Na prática técnica, para tornar essa probabilidade concreta, usa-se o conceito de evento sentinela – que
      corresponde a indicadores objetivos de exposição ou adoecimento precoce. Na área da saúde, um evento sentinela
      é definido como um incidente inesperado que resulta em um dano e exige investigação aprofundada.
    </Paragraph>
    <Paragraph>
      Esses indicadores servem como marcadores científicos da probabilidade. Este PGR mensura e quantifica a
      probabilidade de ocorrência de agravos psicossociais através da análise contínua desses eventos sentinela em
      Grupos Homogêneos de Exposição (GHE), criando uma base para a definição precisa da probabilidade em sua matriz
      de risco.
    </Paragraph>

    <SubSubHeading>7.2.1 Análise Preliminar de Perigo e Riscos</SubSubHeading>
    <Paragraph>
      Esta fase tem como propósito a avaliação antecipada de situações relacionadas a novos projetos, processos,
      produtos, layouts, métodos ou alterações significativas em sistemas já existentes. O objetivo central é
      identificar perigos e estimar os riscos ocupacionais antes da implantação, garantindo a adoção de medidas
      preventivas eficazes.
    </Paragraph>
    <Paragraph>
      É responsabilidade da organização assegurar que toda proposta de modificação ou implantação de novos projetos
      seja submetida a uma análise prévia quanto à presença de perigos e riscos associados, promovendo uma
      abordagem preventiva para proteção da saúde e segurança dos trabalhadores.
    </Paragraph>
    <Paragraph>
      A fim de garantir a validade técnica da amostragem, em conformidade com as normas e evidências científicas da
      ergonomia internacional utilizou-se a lógica do diagnóstico deve ser realizado no GHE, e não no indivíduo, pois
      o risco psicossocial é relacional, coletivo e associado à organização do trabalho.
    </Paragraph>
    <Paragraph>
      Deste modo não é necessário avaliar 100% dos trabalhadores, uma vez que a análise individual é de competência
      do PCMSO (Programa de Controle Médico de Saúde Ocupacional). Já o PGR atua no diagnóstico dos grupos
      homogêneos, identificando padrões organizacionais que configuram exposição a fatores de risco.
    </Paragraph>
    <Paragraph>
      Para isso, adotou-se um modelo de amostragem refinado e orientado por dados, conduzindo a escuta de eventos
      sentinela em 100% do território nacional, abrangendo todas as unidades de negócio e junções da rede de
      agências.
    </Paragraph>

    <Paragraph>Essa abordagem possibilitou:</Paragraph>
    <Bullets
      items={[
        'Estudar o comportamento de cada GHE em diferentes regiões;',
        'Analisar a evolução dos eventos sentinela nos últimos 3 anos;',
        'Comparar a intensidade de exposição de cada GHE com a média nacional da empresa;',
        'Definir estados com maior grau de risco, permitindo maior precisão nas ações futuras.'
      ]}
    />

    <Paragraph>
      O conceito de amostra representativa varia conforme a fase do diagnóstico. Na fase preliminar, a
      representatividade da amostra deve ser compreendida de forma qualitativa, considerando o nível de conhecimento
      sobre a situação de trabalho dos grupos homogêneos (GHEs) e das divisões organizacionais avaliadas. Ou seja, é
      necessário ouvir (por amostragem do GHE) trabalhadores efetivamente inseridos e familiarizados com as
      realidades examinadas.
    </Paragraph>
    <Paragraph>
      Na fase aprofundada, a amostra deve ser estatisticamente representativa em relação à população ativa da
      empresa, contemplando diversidade de função, localização, gênero, idade, origem étnica e tempo de empresa.
    </Paragraph>

    <Paragraph>
      Para garantir a validade estatística da amostragem no diagnóstico psicossocial realizado para o Banco
      Bradesco, foi aplicado o cálculo amostral técnico de Lwanga e Lemeshow, que considera:
    </Paragraph>
    <Bullets
      items={[
        'População finita (número de agências e funcionários por UF);',
        'Média de atestados de saúde ocupacional dos últimos 3 anos;',
        'Intervalo de confiança de 95%;',
        'Erro amostral máximo de 3%.'
      ]}
    />

    <Paragraph>
      Conforme detalhado na metodologia, o critério estatístico é calculado por Unidade Federativa (UF),
      considerando como unidade amostral as agências e como subunidade os funcionários.
    </Paragraph>
    <Paragraph>
      A amostra mínima necessária por estado foi determinada com base no histórico de eventos sentinela,
      principalmente afastamentos e queixas psicossociais, além do número absoluto de trabalhadores por regional.
    </Paragraph>

    <Paragraph>
      Além disso, foram analisadas todas as junções da rede de agências, realizando-se o rastreamento nacional dos
      eventos sentinela em 100% das unidades, o que permitiu:
    </Paragraph>
    <Bullets
      items={[
        'Estudar o comportamento de cada GHE ao longo do tempo;',
        'Identificar estados com maior crescimento de eventos nos últimos 3 anos;',
        'Comparar os resultados de cada GHE com a média nacional da empresa, possibilitando decisões mais precisas e direcionadas.'
      ]}
    />

    <Paragraph>
      Os fatores de risco da severidade são mapeados em visitas presenciais nos ambientes de trabalho de cada GHE
      com escuta ativa dos funcionários e com a avaliação e análise da organização do trabalho, ambiente de
      trabalho, equipamentos (devices), fatores físicos e biomecânicos do trabalho e relacionais, assim como os
      cognitivos.
    </Paragraph>

    <SubSubHeading>7.2.2 Reconhecimento de Perigos e Riscos</SubSubHeading>
    <Paragraph>
      O reconhecimento constitui uma etapa essencial para a caracterização qualitativa dos riscos ocupacionais nos
      diferentes ambientes de trabalho. Envolve a identificação das fontes geradoras de riscos, suas formas de
      propagação, os meios envolvidos, as atividades executadas, os grupos de trabalhadores expostos, definido pelo
      Setor, e o tipo de exposição.
    </Paragraph>
    <Paragraph>
      Essa etapa também contempla a análise de dados históricos da organização, como registros de saúde,
      afastamentos, acidentes e outras informações relevantes que possam indicar impactos à saúde associados aos
      riscos presentes. Além disso, são descritas as medidas de controle já implementadas e avaliada sua eficácia,
      indicando a necessidade de melhorias ou ajustes.
    </Paragraph>

    <SubHeading id="7.3">7.3. Avaliação e Gestão dos Fatores Psicossociais Relacionados ao Trabalho</SubHeading>
    <Paragraph>
      A NR-1 define as diretrizes para a Gestão da Saúde e Segurança no Trabalho (SST), incluindo a obrigatoriedade
      de diagnosticar, avaliar e controlar os riscos psicossociais relacionados ao trabalho, conforme os princípios
      da Ergonomia definidos na NR-17.
    </Paragraph>

    <SubSubHeading>
      7.3.1 Metodologia para Inventário de Risco Relacionada aos Fatores Psicossociais Relacionados ao Trabalho
    </SubSubHeading>
    <Paragraph>
      A NR-1 afirma claramente que os riscos psicossociais devem ser diagnosticados e gerenciados sob o escopo da
      Ergonomia, como demonstrado nos seguintes trechos:
    </Paragraph>
    <Bullets
      items={[
        'Item 1.5.3.1.4: "O gerenciamento de riscos ocupacionais deve abranger os riscos decorrentes dos agentes físicos, químicos, biológicos, riscos de acidentes e riscos relacionados aos fatores ergonômicos, incluindo os fatores de risco psicossociais relacionados ao trabalho."',
        'Item 1.5.3.2.1: "A organização deve considerar as condições de trabalho, nos termos da NR-17, incluindo os fatores de risco psicossociais relacionados ao trabalho."'
      ]}
    />

    <Paragraph>
      Além disso, o nível de risco ocupacional deve ser identificado com base na combinação entre severidade e
      probabilidade:
    </Paragraph>
    <Bullets
      items={[
        'Item 1.5.4.4.2: "Para cada risco deve ser indicado o nível de risco ocupacional, determinado pela combinação da severidade das possíveis lesões ou agravos à saúde com a probabilidade de sua ocorrência."'
      ]}
    />

    <Paragraph>
      Somente pela combinação da Severidade e da Probabilidade podemos atribuir a presença do Risco Psicossocial
      relacionado ao trabalho. Esta afirmação também esta presente na ISO 45.003:2014 que trata da gestão para estes
      fatores.
    </Paragraph>
    <Paragraph>
      Para evidenciar a Matriz de Risco é necessário a aplicação de metodologia científica que atenda aos critérios
      normativos, mas principalmente, que seja capaz de entregar um resultado efetivo para cuidar genuinamente de
      pessoas e empresas. Esse método é o ERGOCHECK – PREMAPA, desenvolvido pela Internacional Ergonomics School
      (EPM).
    </Paragraph>
    <Paragraph>
      A metodologia atende plenamente aos critérios da NR-1, sendo também compatível com os requisitos da Norma ISO
      45003:2021, que orienta sobre a Gestão de Fatores Psicossociais no local de trabalho dentro de um Sistema de
      Gestão de Segurança e Saúde Ocupacional (SGSSO).
    </Paragraph>
    <Paragraph>
      O método adota padrões estabelecidos pela Comissão Consultiva do Ministério do Trabalho Europeu, criada em
      2010 com base no Decreto Legislativo nº 81/2008 (art. 6, §8, letra m, e art. 28, §1), que tornou obrigatória a
      avaliação do estresse relacionado ao trabalho como parte da avaliação geral de riscos (VDR).
    </Paragraph>
    <Paragraph>
      A norma Européia, ou uma ferramenta desenvolvida na Europa é capaz de atender aos critérios diagnósticos e de
      gestão necessários ao nosso país. Em primeiro lugar cabe elucidar que o Brasil é signatário de diversos
      acordos internacionais voltados à promoção da saúde e segurança do trabalhador, alinhando-se às diretrizes e
      boas práticas adotadas por países da União Europeia e organismos multilaterais, como a Organização
      Internacional do Trabalho (OIT).
    </Paragraph>
    <Paragraph>
      O Acordo Europeu possui critérios claros e metodologias padronizadas para a avaliação dos fatores de risco
      psicossociais no ambiente de trabalho, com foco na proteção da saúde mental dos trabalhadores.
    </Paragraph>
    <Paragraph>
      Entre os principais critérios adotados, o governo Europeu definiu a obrigatoriedade da realização de
      avaliações preliminares e aprofundadas.
    </Paragraph>

    <Paragraph>A avaliação preliminar foca em identificar indicadores objetivos e verificáveis, como:</Paragraph>
    <Bullets
      items={[
        'Eventos sentinelas (dados de absenteísmo, turnos, acidentes de trabalho...);',
        'Fatores de conteúdo (como carga de trabalho e complexidade das tarefas...);',
        'Fatores de contexto (relacionados à organização, suporte social e ambiente de trabalho...).'
      ]}
    />

    <Paragraph>
      Caso esses indicadores apontem para um risco elevado de estresse, uma avaliação mais aprofundada deve ser
      realizada, aí sim com especialistas em casos pontuais, com a inclusão da percepção dos trabalhadores sobre as
      condições de trabalho e a organização.
    </Paragraph>
    <Paragraph>
      Os mesmos critérios do acordo Europeu são utilizados pelo Método ERGOCHECK PREMAPA, conforme segue abaixo:
    </Paragraph>

    <SubSubHeading>CONTEÚDO DO TRABALHO</SubSubHeading>
    <Bullets
      items={[
        'Fator 1 = Considera o ambiente e as ferramentas de trabalho nos últimos 2 anos',
        'Fator 2 = Considera a carga e o ritmo de trabalho',
        'Fator 3 = Considera os horários de trabalho',
        'Fator 4 = Se a atividade prevê contato com sofrimento humano',
        'Fator 5 = Se a atividade prevê contato direto com colegas e atendimento ao público, ainda se requer frequentemente assumir problemas difíceis de resolver',
        'Fator 6 = Se a atividade apresenta risco potencial de assaltos e/ou agressão'
      ]}
    />

    <SubSubHeading>CONTEXTO DO TRABALHO</SubSubHeading>
    <Bullets
      items={[
        'Fator 7 = Autonomia de decisão e controle durante o trabalho',
        'Fator 8a = Relações Interpessoais',
        'Fator 8b = Isolamento Físico',
        'Fator 9 = Conciliar trabalho e atividades pessoais'
      ]}
    />

    <SubSubHeading>EVENTOS SENTINELA</SubSubHeading>
    <Bullets items={['Fator 10 = Dados de segurança, dados de absenteísmo, turnos, acidentes de trabalho']} />

    <Paragraph>
      O conceito de amostra representativa difere conforme a fase do diagnóstico: se é uma avaliação preliminar ou
      uma avaliação aprofundada.
    </Paragraph>
    <Paragraph>
      Por isso esse relatório traz o estudo dos eventos sentinela, a fim de iniciar a delimitação das amostras
      conforme a sua necessária significância para estabelecer resultados que desencadeiem em programas de saúde
      para os trabalhadores.
    </Paragraph>
    <Paragraph>
      Na fase de avaliação preliminar, a representatividade da amostra de trabalhadores deve ser entendida num
      sentido qualitativo, em relação ao nível de conhecimento da situação de trabalho dos GHEs que estão a ser
      avaliados.
    </Paragraph>
    <Paragraph>
      Na fase de avaliação aprofundada, a amostra de trabalhadores deve ser estatisticamente representativa em
      relação à população ativa da empresa, não só no que se refere à localização do posto de trabalho e à
      distinção em grupos homogêneos, mas também no que se refere a aspectos pessoais como diferenças de género,
      idade, origem étnica, etc.
    </Paragraph>
    <Paragraph>Definida a amostra por GHE seguimos para as fases de avaliação dos Riscos Psicossociais relacionados ao Trabalho.</Paragraph>

    <SubSubHeading>
      7.3.2. Avaliação dos Riscos e Exposição dos Trabalhadores – Risco Psicossocial Relacionado ao Trabalho
    </SubSubHeading>
    <Paragraph>Natureza da tarefa é a definição do GHE.</Paragraph>
    <Paragraph>
      Grupo Homogêneo de Exposição (GHE) é definido como o conjunto de trabalhadores submetidos à mesma situação de
      trabalho, às mesmas condições de trabalho e à mesma organização do trabalho.
    </Paragraph>
    <Paragraph>
      Quando o foco é nos fatores psicossociais, é essencial destacar que as relações hierárquicas podem modificar a
      definição dos GHEs, que anteriormente eram estabelecidos de forma mais "macro".
    </Paragraph>
    <Paragraph>
      Assim, conforme estabelece a NR-1, torna-se necessário especificar com precisão o grupo real de trabalhadores
      expostos aos fatores de risco psicossociais.
    </Paragraph>
    <Paragraph>
      No contexto da rede de agências, compreende-se como GHE aqueles cargos que possuem a mesma natureza de tarefas
      e, consequentemente, a mesma forma de exposição.
    </Paragraph>
    <Paragraph>
      A avaliação psicossocial é obrigatória para todos os trabalhadores, inclusive líderes e supervisores, e deve
      ser feita por grupos homogêneos, definidos pela organização. Ela ocorre em duas fases:
    </Paragraph>
    <Bullets
      items={[
        'Avaliação Preliminar (sempre obrigatória)',
        'Investigação Adicional (obrigatória apenas se forem identificados riscos relevantes)'
      ]}
    />

    <Paragraph>Na fase preliminar, são analisados os três grandes grupos de indicadores, conforme mencionado acima:</Paragraph>
    <Bullets
      items={['I. Eventos Sentinela', 'II. Fatores de Conteúdo do Trabalho', 'III. Fatores do Contexto do Trabalho']}
    />

    <Paragraph>
      Cada fator detectado recebe um peso específico no cálculo do risco, conforme a relevância científica da sua
      associação com o estresse ocupacional, conforme estudos internacionais e modelos consolidados da literatura.
    </Paragraph>

    <Separator weight={15} />
  </View>
)

export default MetodologiaFisicoQuimicoErgonomicoSection
